import React, { Component } from "react"
import { Column, CellMeasurerCache, SortDirection } from "react-virtualized"
import PropTypes from "prop-types"
import cloudinary from "cloudinary-core"
import moment from "moment"
import FontAwesome from "react-fontawesome"

import { timeBetween } from "../../helpers/sharedHelperFunctions"
import AssetItemCard from "../AssetItemCard"
import DataTable, { tooltipHeaderRenderer } from "@swipesense/data-table"
import Loading from "../Loading"

import "./LowBatteryTableVirtualized.scss"

class LowBatteryTableVirtualized extends Component {
  constructor(props) {
    super(props)

    this.cache = new CellMeasurerCache({
      minHeight: 160,
      fixedWidth: true,
    })
    this.state = {}
  }

  assetRenderer = ({ rowData }) => {
    const asset = rowData

    // image-related code
    const cloudinaryCore = new cloudinary.Cloudinary({
      cloud_name: "swipesense-inc",
    })
    const fallbackUrl = "/icons/fallback.svg"
    const photoUrl = asset.imageId
      ? cloudinaryCore.url(asset.imageId, {
          width: 60,
          height: 60,
          crop: "fill",
        })
      : fallbackUrl
    return [
      <div key={asset.id} className="low-battery-asset-cell">
        <img
          width="60"
          height="60"
          src={photoUrl}
          onError={e => {
            e.target.src = fallbackUrl
          }}
          alt="Asset"
        />
        <div className="low-battery-asset-info">
          <div className="asset-name">{asset.name}</div>
          <div className="asset-mfg">
            {asset.manufacturer} - {asset.modelName}
          </div>
          <div className="asset-ids">
            <div className="asset-id">
              <div className="id-rectangle">A</div>
              {asset.assetId}
            </div>
            {asset.serialNumber ? (
              <div className="asset-sn">
                <div className="id-rectangle">S</div>
                {asset.serialNumber}
              </div>
            ) : null}
          </div>
        </div>
      </div>,
    ]
  }

  ownershipRenderer = ({ rowData, cellData }) => {
    const asset = rowData
    return [
      <div key={asset.id} className="department-owners">
        {asset.owners.map((owner, i) => (
          <div className="owner-name" key={i}>
            {owner.name}
          </div>
        ))}
        {cellData === "rented" ? (
          <div>
            <FontAwesome className="rental-tag" name={"tag"} />
            Rental
          </div>
        ) : null}
      </div>,
    ]
  }

  locationRenderer = ({ rowData, cellData }) => {
    return [
      <div key={rowData.id} className="low-battery-location">
        <div>
          <div>{cellData.unit.name}</div>
          <div>{cellData.name}</div>
        </div>
      </div>,
    ]
  }

  timeRenderer = ({ rowData, cellData }) => {
    const timeSinceDetected = timeBetween(Date.parse(cellData), Date.now())
    return [
      <div key={rowData.id} className="low-battery-time">
        {moment.duration(timeSinceDetected).humanize()}
      </div>,
    ]
  }

  batteryStatusRenderer = ({ rowData, cellData }) => {
    if (cellData === "low") {
      return <div>Low battery</div>
    }
    if (cellData === "dying") {
      return <div>Out of battery</div>
    }
    return <div key={rowData.id}>{cellData}</div>
  }

  editAssetRenderer = ({ cellData }) => {
    return [
      <a
        href={`https://admin.swipesense.com/asset/${cellData}/edit`}
        target="blank"
        className="edit-asset-link"
      >
        Edit Asset
      </a>,
    ]
  }

  render() {
    const sortedAssets = this.props.lowBatteryAssets
      .map(search => search.item)
      .sort((assetA, assetB) => {
        if (!this.state.sortBy || !this.state.sortDirection) {
          return 0
        }
        const a = assetA[this.state.sortBy]
        const b = assetB[this.state.sortBy]
        /* istanbul ignore next */
        if (a < b) {
          return this.state.sortDirection === "DESC" ? 1 : -1
        }
        /* istanbul ignore next */
        if (b < a) {
          return this.state.sortDirection === "DESC" ? -1 : 1
        }
        return 0
      })

    return (
      <DataTable
        data={sortedAssets}
        rowHeight={90}
        className="LowBatteryTableVirtualized"
        noRowsMessage="Congratulations! All of your Asset Tag batteries are in great shape."
        loadingComponent={<Loading />}
        onSortChange={(sortBy, sortDirection) =>
          this.setState({ sortBy, sortDirection })
        }
      >
        <Column
          width={150}
          dataKey="id"
          disableSort={true}
          headerClassName="table-header"
          label="ASSET"
          cellRenderer={this.assetRenderer}
        />
        <Column
          width={25}
          dataKey="ownership"
          disableSort={true}
          headerClassName="table-header"
          label="OWNERSHIP"
          cellRenderer={this.ownershipRenderer}
        />
        <Column
          width={25}
          dataKey="location"
          disableSort={true}
          headerClassName="table-header"
          label="LAST LOCATION"
          cellRenderer={this.locationRenderer}
        />
        <Column
          width={25}
          dataKey="lastSeenAt"
          headerClassName="table-header"
          label="LAST SEEN"
          cellRenderer={this.timeRenderer}
        />
        <Column
          width={25}
          dataKey="batteryStatus"
          headerClassName="table-header"
          label="BATTERY STATUS"
          cellRenderer={this.batteryStatusRenderer}
          defaultSortDirection={SortDirection.ASC}
          headerRenderer={tooltipHeaderRenderer(
            'Tags with "Low Battery" will continue to operate normally for at least another 3-5 weeks. Tags that are "Out of Battery" may no longer operate. All Tags in this list should be replaced as soon as possible.'
          )}
        />
        <Column
          width={25}
          dataKey="id"
          headerClassName="table-header"
          label="REPLACE TAG"
          cellRenderer={this.editAssetRenderer}
        />
      </DataTable>
    )
  }
}

LowBatteryTableVirtualized.propTypes = {
  lowBatteryAssets: PropTypes.arrayOf(
    PropTypes.shape({ item: PropTypes.shape(AssetItemCard.propTypes) })
  ),
  deviceType: PropTypes.string.isRequired,
}

export default LowBatteryTableVirtualized
